import React from 'react';
import styles from './DataTable.module.css';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import useWindowSize from '@/lib/useWindowSize';

export type DataTableHeader = {
  label: string;
  column?: string;
  render?: (row: DataTableRow) => React.ReactNode;
  width?: string;
  mobileLabel?: string;
  mobileRender?: (row: DataTableRow) => React.ReactNode;
  mobileHidden?: boolean;
  mobileOrder?: number;

  stickLeft?: boolean;
  stickRight?: boolean;
  alignRight?: boolean;
  alignCenter?: boolean;
  alignLeft?: boolean;
};

export type DataTableRow = any;

type DataTableProps = {
  isLoading?: boolean;
  cabecalhos: DataTableHeader[];
  linhas?: DataTableRow[];
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  rowSize?: 'sm' | 'md' | 'lg';
  rowStyle?: (row: DataTableRow) => React.CSSProperties;
  /**
   * Conteúdo expandido logo abaixo da linha (detalhes, diagnóstico). Retorne
   * null para as linhas que não devem expandir — quem controla qual está
   * aberta é o chamador.
   */
  renderSubRow?: (row: DataTableRow) => React.ReactNode;
  paginas?: any[];
  aoObterPagina?: (pagina: any) => DataTableRow[];
};

const DataTable = ({
  isLoading,
  cabecalhos,
  linhas,
  onLoadMore,
  hasMoreData,
  paginas,
  aoObterPagina,
  rowSize = 'md',
  rowStyle,
  renderSubRow,
}: DataTableProps) => {
  const [, , isMobile] = useWindowSize();

  const resolvedRows = React.useMemo(() => {
    if (linhas) {
      return linhas;
    }

    if (paginas && aoObterPagina) {
      return paginas.flatMap((pagina) => aoObterPagina(pagina) ?? []);
    }

    return [];
  }, [aoObterPagina, linhas, paginas]);

  // Um pedido de "mais" por ciclo: desarma ao chamar onLoadMore e só rearma
  // quando o chamador sinaliza que a carga começou (isLoading) ou as linhas
  // mudaram. Evita disparo duplo quando o scroll e o sentinela coincidem.
  const armedRef = React.useRef(true);
  React.useEffect(() => {
    armedRef.current = true;
  }, [isLoading, resolvedRows.length]);

  const tryLoadMore = React.useCallback(() => {
    if (!hasMoreData || isLoading || !onLoadMore || !armedRef.current) return;
    armedRef.current = false;
    onLoadMore();
  }, [hasMoreData, isLoading, onLoadMore]);

  const handleScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - 100 <= el.clientHeight) {
        tryLoadMore();
      }
    },
    [tryLoadMore],
  );

  // Sentinela no fim da lista: carrega mais quando entra na área visível, seja
  // qual for o ancestral que rola (o próprio container ou a página inteira,
  // caso do mobile) — e também quando a primeira página não preenche a tela,
  // situação em que o onScroll acima nunca dispararia.
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMoreData || isLoading || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) tryLoadMore();
      },
      { rootMargin: '120px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreData, isLoading, tryLoadMore, resolvedRows.length]);

  const mobileHeaders = React.useMemo(
    () => cabecalhos
      .map((header, index) => ({ ...header, __index: index }))
      .filter((header) => !header.mobileHidden)
      .sort((a, b) => (a.mobileOrder ?? a.__index) - (b.mobileOrder ?? b.__index)),
    [cabecalhos],
  );

  const renderCellContent = React.useCallback((header: DataTableHeader, row: DataTableRow, mobile = false) => {
    if (mobile && header.mobileRender) {
      return header.mobileRender(row);
    }

    if (header.render) {
      return header.render(row);
    }

    return header.column ? row[header.column] : null;
  }, []);

  const renderLinha = (linha: DataTableRow, lidx: number) => {
    const subRow = renderSubRow?.(linha);
    return (
      <React.Fragment key={`linha-${lidx}`}>
        <tr style={rowStyle?.(linha)} className={[styles.row, styles[`rowSize-${rowSize}`]].join(' ')}>
          {cabecalhos.map((it, index) => (
            <td
              className={[
                it.stickLeft
                  ? styles.stickLeft
                  : it.stickRight
                    ? styles.stickRight
                    : null,
                it.alignRight
                  ? styles.alignRight
                  : it.alignCenter
                    ? styles.alignCenter
                    : it.alignLeft
                      ? styles.alignLeft
                      : null,
              ].join(' ')}
              key={`linha-${lidx}-col-${index}`}
            >
              {renderCellContent(it, linha)}
            </td>
          ))}
        </tr>
        {subRow ? (
          <tr style={rowStyle?.(linha)}>
            <td colSpan={cabecalhos.length} style={{ padding: 0 }}>
              {subRow}
            </td>
          </tr>
        ) : null}
      </React.Fragment>
    );
  };

  const renderLinhaMobile = (linha: DataTableRow, lidx: number) => (
    <article
      key={`linha-mobile-${lidx}`}
      style={rowStyle?.(linha)}
      className={[styles.mobileRow, styles[`mobileRowSize-${rowSize}`]].join(' ')}
    >
      {mobileHeaders.map((it, index) => {
        const content = renderCellContent(it, linha, true);
        if (content === null || content === undefined || content === false) {
          return null;
        }

        const label = it.mobileLabel ?? it.label;

        return (
          <div
            key={`linha-mobile-${lidx}-col-${index}`}
            className={[styles.mobileField, !label ? styles.mobileFieldNoLabel : null].filter(Boolean).join(' ')}
          >
            {label ? <span className={styles.mobileLabel}>{label}</span> : null}
            <div className={styles.mobileValue}>{content}</div>
          </div>
        );
      })}
      {renderSubRow?.(linha) ?? null}
    </article>
  );

  return (
    <div className={styles.container} onScroll={handleScroll}>
      {isMobile ? (
        <div className={styles.mobileTable}>
          {resolvedRows.length > 0 && resolvedRows.map(renderLinhaMobile)}

          {hasMoreData && <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />}

          {!isLoading && !hasMoreData && (
            <div className={styles.mobileStatusCard}>
              Isso é tudo.
            </div>
          )}

          {isLoading && resolvedRows.length > 0 && (
            <div className={styles.mobileStatusCard}>
              <Icon
                path={mdiLoading}
                size={1}
                color="var(--tc-color-gray-700)"
                spin
              />
              Carregando mais dados...
            </div>
          )}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr className={[styles.headerRow, styles[`rowSize-${rowSize}`]].join(' ')}>
              {cabecalhos?.map((it, lidx) => (
                <th
                  key={`header-${lidx}`}
                  className={[
                    it.stickLeft
                      ? styles.stickLeft
                      : it.stickRight
                        ? styles.stickRight
                        : null,
                  ].join(' ')}
                  style={{ width: it.width }}
                >
                  {it.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resolvedRows.length > 0 && resolvedRows.map(renderLinha)}

            {hasMoreData && (
              <tr aria-hidden>
                <td colSpan={cabecalhos?.length} style={{ padding: 0, border: 'none' }}>
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </td>
              </tr>
            )}

            {!isLoading && !hasMoreData && (
              <tr className={[styles.row, styles[`rowSize-${rowSize}`]].join(' ')}>
                <td
                  colSpan={cabecalhos?.length}
                  style={{
                    textAlign: 'center',
                    backgroundColor: 'var(--tc-color-gray-200)',
                  }}
                >
                  Isso é tudo.
                </td>
              </tr>
            )}
            {isLoading &&
              resolvedRows.length > 20 &&
              Array.from({ length: 20 }).map((_, idx) => (
                <tr key={idx} className={[styles.row, styles.skeletonRow, styles[`rowSize-${rowSize}`]].join(' ')}>
                  <td colSpan={cabecalhos?.length}>
                    <div className={styles.skeletonContainer}>
                      <span
                        className={[styles.skeleton, styles.skeletonA].join(' ')}
                      ></span>
                      <span
                        className={[styles.skeleton, styles.skeletonB].join(' ')}
                      ></span>
                      <span
                        className={[styles.skeleton, styles.skeletonC].join(' ')}
                      ></span>
                    </div>
                  </td>
                </tr>
              ))}
            {isLoading && resolvedRows.length > 0 && (
              <tr className={[styles.row, styles.skeletonRow, styles[`rowSize-${rowSize}`]].join(' ')}>
                <td
                  colSpan={cabecalhos?.length}
                  style={{
                    textAlign: 'center',
                    backgroundColor: 'var(--tc-color-gray-200)',
                  }}
                >
                  <Icon
                    path={mdiLoading}
                    size={1}
                    color="var(--tc-color-gray-700)"
                    spin
                  />{' '}
                  Carregando mais dados...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DataTable;
